<template>
  <!-- "expand-on-hover" :rail="useRail" "permanent" to close drawer-->
  <v-navigation-drawer id="tech_side_drawer" elevation="0" v-model="props.drawerOpen" rail z-index="100000" expand-on-hover location="left">
    <div class="tech_drawer_div">
      <!---------------------------------------------->
      <!-- Side Menu Navigation Drawer Header Items -->
      <v-list class="tech_hero_image">
        <v-list-item class="tech_hero_avatar pl-1" density="compact" :prepend-avatar="getResourceUrl(`/cdn/${sportTitle}/site/logo-icon.svg`)" :to="'/'">
          <template v-slot:title>
            <img class="tech_hero_logo" :src="getResourceUrl(`/cdn/${sportTitle}/site/logo-word.svg`)" :alt="`${sportTitle}Levels`" />
          </template>
        </v-list-item>
      </v-list>
      <!------------------------------------->
      <!-- Side Menu Navigation List Items -->
      <v-list dense nav class="side_menu_container pa-0">
        <!-- Skeleton Loader shown while loading -->
        <div v-if="loading">
          <v-skeleton-loader class="menu_skeleton_loader px-0" width="255px" max-width="350" color="transparent" type="list-item-avatar" v-for="n in 7" :key="n"> </v-skeleton-loader>
        </div>
        <!------------------------------------------->
        <!-- Container to hold all side menu items -->
        <div v-else>
          <span class="tech_hero_subNav">
            <p class="tech_hero_subtitle ma-0">Popular In App</p>
            <button @click="toggleDrawer" class="tech_hero_close pa-0 ma-0">
              <v-icon icon="mdi-close" size="large" color="white"></v-icon>
            </button>
          </span>
          <!------------------------------------------------------->
          <!-- Actual Side Menu Items shown when loading is done -->
          <v-list-item
            v-for="(item, index) in props.leftMenuItems"
            :key="index"
            class="side_menu_item title weg_translate"
            base-color="white"
            :to="item.href && !item.href.startsWith('http') ? '/' + item.href : undefined"
            :href="item.href && item.href.startsWith('http') ? item.href : undefined"
            :target="item.href && item.href.startsWith('http') ? '_blank' : undefined"
            :prepend-avatar="getResourceUrl('/' + item.img)"
            :append-icon="item.href && item.href.startsWith('http') ? 'mdi-open-in-new' : ''"
            :title="item.menu_item">
            <v-tooltip activator="parent" location="bottom" class="sidenav_tooltip hidden-xs">{{ item.title }}</v-tooltip>
          </v-list-item>
        </div>
      </v-list>
    </div>
    <div class="tech_register_div">
      <!------------------------------------------>
      <!-- Create NGB Level Buttons if logged In -->
      <!-- <v-list-item v-if="$attrs.isLoggedOut" id="tech_action_ngb" class="side_menu_item title" :href="ngbItem.href" :prepend-avatar="String(ngbItem.img || '')" append-icon="mdi-open-in-new" color="white" base-color="white" style="padding-inline: 8px" target="blank">
    <v-btn class="tech_action_button" color="transparent" variant="text" :href="ngbItem.href" :ripple="false" width="95%" target="blank">
      {{ ngbItem.text }}
    </v-btn>
  </v-list-item> -->
      <v-list-item
        v-if="$attrs.isLoggedIn"
        id="tech_action_ngb"
        class="side_menu_item title weg_translate"
        :href="ngbItem.href"
        :prepend-avatar="ngbItem.img"
        append-icon="mdi-open-in-new"
        color="white"
        base-color="white"
        style="padding-inline: 8px"
        target="_blank">
        <v-btn class="tech_action_button" color="transparent" variant="text" :href="ngbItem.href" :ripple="false" width="95%" target="_blank">
          {{ ngbItem.text }}
        </v-btn>
      </v-list-item>

      <!-- Create Registration Buttons if logged Out -->
      <v-list-item v-if="$attrs.isLoggedOut" class="side_menu_item title weg_translate" to="/login" prepend-icon="mdi-login" base-color="white" style="padding-inline: 8px">
        <v-btn class="tech_primary_button" variant="flat" color="primary-800" to="login" :ripple="true" width="95%"> Login </v-btn>
      </v-list-item>
      <v-list-item
        v-if="$attrs.isLoggedOut"
        class="side_menu_item title weg_translate"
        to="/register"
        :prepend-avatar="getResourceUrl(`/maya/icons/icon-player-white.svg`)"
        color="white"
        style="padding-inline: 8px">
        <v-btn class="tech_action_button" variant="flat" color="action" to="register" :ripple="true" width="95%"> Create Account </v-btn>
      </v-list-item>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { PropType, computed } from 'vue';
import { useUserStore } from '@/store/userJWTStore';
import { useUserDataStore } from '@/store/userDataStore';
import { getResourceUrl, sportTitle } from '@/services/admin_actions/apiUtils';

// Initialize the Stores
const store = useUserStore(); // Initialize the JWT Store
const userDataStore = useUserDataStore(); // Access the User Data store

// Define the props for the component
const props = defineProps({
  leftMenuItems: { type: Object, default: () => {} },
  memberData: { type: Object, default: () => ({}) },
  myRankingsData: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: true },
  drawerOpen: Boolean,
  toggleDrawer: {
    type: Function as PropType<(event?: MouseEvent | KeyboardEvent) => void>,
    required: false, // toggleDrawer is not required by default
    default: () => {}, // Provide a default no-op function to handle undefined cases
  },
});

/* ------------------------------------------------ */
// Computed property to get NGB data from the siteMap

const ngbItem = computed(() => {
  const siteMap = userDataStore.menuData.site_map;

  // console.groupCollapsed('NGB Data Evaluation');
  // console.log('Complete Site Map:', siteMap);

  // First check: Directly return the 'NGB' item if it exists and has properties
  if (siteMap['NGB'] && Object.keys(siteMap['NGB']).length > 0) {
    // console.log('Using primary NGB data:', siteMap['NGB']);
    // console.groupEnd();
    return siteMap['NGB'];
  }

  // Second check: Attempt to fetch nested or differently structured 'NGB' data
  if (siteMap['NGB'] && siteMap['NGB'].href) {
    const nestedData = {
      href: siteMap['NGB'].href,
      img: siteMap['NGB'].img,
      mobile: siteMap['NGB'].mobile,
      pos: siteMap['NGB'].pos,
      text: siteMap['NGB'].text,
      title: siteMap['NGB'].title,
    };

    if (nestedData.href && nestedData.img && nestedData.text) {
      // console.log('Using nested NGB data:', nestedData);
      // console.groupEnd();
      return nestedData;
    }
  }

  // Third fallback: Static data if no other data is available
  const staticData = {
    img: getResourceUrl('/maya/icons/icon-player-white.svg'),
    mobile: true,
    text: 'No NGB Set',
    href: '/',
  };
  // console.log('Using static fallback data:', staticData);
  // console.groupEnd();
  return staticData;
});

/* End */
</script>

<style lang="scss">
// import the Sass Variables File

/* -------------------------- */
/* Navigation Drawer Styling */
#tech_side_drawer {
  background-color: rgb(var(--v-theme-primary));
  z-index: 10000 !important; // Ensure the drawer is always on top except for the bottom nav
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;

  &__content {
    min-width: 255px;
    gap: 20px;
    overflow: visible;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 0;
    box-sizing: border-box;
    position: relative;
  }

  .v-list-item-title {
    transition: opacity 0.3s ease;
    opacity: 1;
    @media screen and (min-width: $mobile-screen) {
      opacity: 0;
    }
  }

  &:hover .v-list-item-title {
    opacity: 1;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    width: 5px;
    background-color: rgb(var(--v-theme-scrollbar));
    border-radius: 3px;
    filter: saturate(3);
  }
}

.v-navigation-drawer__scrim {
  display: none !important; // Hide the scrim when the drawer is open
}

.side_menu_container {
  padding: 0px;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: space-between;
  align-items: stretch;
  overflow-y: auto !important; // Added to fix the scroll issue
}

.tech_hero_image {
  padding: 0px !important;
  min-height: 64px;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  align-items: start;
}

.tech_hero_avatar {
  border: solid;
  border-width: 0px 0px 0px 0px;
  border-color: rgb(var(--v-theme-primary-800));
  .v-avatar {
    height: 29px !important;
    width: 29px !important;
    margin: 8px 10px 8px 0px;
    margin-inline-end: 8px !important;
  }
  .v-list-item__spacer {
    display: none;
  }
}

.v-list-item.tech_hero_avatar {
  padding: 0;
  width: 100%;
  .v-list-item__overlay {
    display: none;
  }
}

.v-list-item-title .tech_hero_logo {
  height: 24px;
  object-fit: contain;
  margin-top: 5px;
}

.tech_hero_close {
  display: none;
}

.v-list-item-title {
  display: flex;
}

#tech_side_drawer .v-list-item--nav {
  margin: 0px !important;
  padding-right: 20px;
  border: solid;
  border-width: 1px 0px 0px 0px;
  border-color: rgb(var(--v-theme-primary-800));
  border-radius: 0%;
  min-height: 50px !important;
  max-height: 50px !important;
  display: flex;
}

#tech_side_drawer .v-list-item__prepend > .v-avatar {
  margin-inline-start: 8px;
  margin-inline-end: 16px;
}

/* Navigation Avatar Styling */
#tech_side_drawer .v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline-start: 8px;
  padding-inline-end: 16px;
}

.mdi-menu::before {
  color: rgb(var(--v-theme-white));
}
/* Ends */

/* ------------------------------ */
/*Navigation Font Styling Starts */

#tech_side_drawer .v-list-item--nav .v-list-item-title {
  // font-family: $brandon-grotesque-font;
  font-size: 13px;
  text-transform: uppercase;
  text-align: left;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: 2.25px;
  color: rgb(var(--v-theme-white));
  text-wrap: balance;
}
/* Ends */

/* ------------------------------ */
// Styling for the side menu div container

// Ensure the side menu items scroll past the bottom of the drawer
.tech_drawer_div {
  overflow: auto; /* Allows scrolling */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  -ms-overflow-style: none; /* Hide scrollbar for Internet Explorer and Edge */
  min-height: 100%;
  box-sizing: border-box; // Ensure the height is calculated correctly including padding and border
  z-index: 1;
  // set the padding and margin for the drawer div based on the number of items in the tech_register_div
  &:has(+ .tech_register_div > .v-list-item) {
    padding-bottom: 55px;
    margin-bottom: -55px;
  }
  &:has(+ .tech_register_div > .v-list-item + .v-list-item) {
    padding-bottom: 110px;
    margin-bottom: -110px;
  }
  &:has(+ .tech_register_div > .v-list-item + .v-list-item + .v-list-item) {
    padding-bottom: 165px;
    margin-bottom: -175px;
  }
}

.tech_drawer_div::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit browsers (Chrome, Safari, Opera) */
}

/* Ends */

/* ------------------------------ */
// Side Menu Skeleton Loader Styling
.menu_skeleton_loader {
  .v-skeleton-loader__avatar {
    margin: 10px 20px 5px 11px;
    max-height: 30px;
    min-height: 30px;
    height: 30px;
    max-width: 30px;
    min-width: 30px;
    width: 30px;
  }

  .v-skeleton-loader__bone {
    padding: 3px 0px;
    margin-top: 5px;
  }

  .v-skeleton-loader__text {
    margin-top: 16px;
  }

  // Styling for skeleton loader to change shimmer effect
  .v-skeleton-loader__bone::after {
    @include global_skeleton_loader;
    // animation: loading 2.5s infinite !important;
    // background: linear-gradient(90deg, rgba(var(--v-theme-surface), 0), rgba(var(--v-theme-surface), 0.03), rgba(var(--v-theme-surface), 0)) !important;
  }
}

/* ------------------------------ */
// Styling for the Tooltip in the Side Menu
#tech_side_drawer .v-overlay-container .v-overlay.v-tooltip {
  z-index: 10000 !important;
  right: 0;

  .v-overlay__content {
    border: 1px solid white;
    background-color: black;
    opacity: 0;
    animation: toolfade;
    animation-delay: 1s;
    box-shadow: 0px 3px 4px 1px rgba(0, 0, 0, 0.5);
  }

  &.sidenav_tooltip {
    .v-overlay__content {
      left: 16px !important;
      transform: translateY(-54%);
    }
  }

  &.v-overlay--active {
    .v-overlay__content {
      padding: 4px 12px;
      animation-duration: 0.5s;
      animation-iteration-count: 1;
      animation-direction: forwards;
      animation-fill-mode: forwards;
    }
  }
}

/* ------------------------------ */
// Toolfade Animation for Tooltip
@keyframes toolfade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Styling for the Side Menu Sub Title
.tech_hero_subtitle {
  color: rgb(var(--v-theme-secondary));
  padding: 20px;
  border: solid;
  border-width: 0px;
  border-color: rgb(var(--v-theme-primary-800));
  text-transform: uppercase;
  font-family: $brandon-grotesque-font;
  font-size: 10px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 4;
  letter-spacing: 1px;
  display: none;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  max-height: 20px;
}

/* --------------------------------- */
// Styling for the Register Buttons Div
.tech_register_div {
  padding: 5px 10px 5px 0px;
  margin: 0px;
  border: solid;
  border-width: 1px 0px 0px 0px;
  border-color: rgb(var(--v-theme-primary-800));
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-sizing: border-box;
  background-color: rgb(var(--v-theme-primary));
  z-index: 2;
}

#tech_action_ngb {
  .v-btn__content {
    color: #fff;
    line-height: 2.6;
  }
}
/* End */

/* --------------------------------- */
// Styling for the side menu items
.side_menu_item {
  background-color: transparent;
  // Ensure the side menu items are displayed at full width
  .v-list-item__content {
    width: 100%;
  }
  // Set the height for the v-avatar
  .v-avatar.v-avatar--size-default {
    --v-avatar-height: 20px !important;
    border-radius: 0px;
  }
  // Remove the overlay from active side menu items
  .v-list-item__overlay {
    display: none;
  }
  // Hide the default extra spacer in the side menu items
  .v-list-item__spacer {
    display: none;
  }
  // Control size and padding for side menu icons
  .v-list-item__prepend > .v-icon {
    font-size: 25px;
    margin-inline-start: 5px;
    margin-inline-end: 14px;
    opacity: 1;
  }
  .v-list-item__append {
    min-width: 40px;
    justify-content: flex-end;
  }
}
</style>
